import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { FileText, Sparkles, Award, Globe2, Cpu, Layers, ShieldCheck, Target, Compass } from "lucide-react";
import { Section, SectionHeading, Reveal } from "../lib/ui";
import { DELIVERABLES, APPROACH, WHY } from "../config";

const GOLD_TEXTURE = "https://images.unsplash.com/photo-1639674242803-a9de33b3a835?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

// 13 — Strategic Deliverables
export function Deliverables() {
  return (
    <Section id="deliverables" className="bg-ink">
      <SectionHeading
        overline="Strategic Outputs"
        title={<>Key Strategic Deliverables <span className="gold-text">You Can Produce.</span></>}
        kicker="Potential strategic outputs developed according to client requirements."
      />
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {DELIVERABLES.map((d, i) => (
          <Reveal key={d.code} delay={(i % 3) * 0.08}>
            <div className="group relative h-full rounded-xl bg-ink-surface border border-white/10 p-7 overflow-hidden hover:border-gold/40 transition-colors duration-500" data-testid={`deliverable-${i}`}>
              <img src={GOLD_TEXTURE} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-10 transition-opacity duration-700" />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-widest text-gold/60">{d.code}</span>
                  <FileText size={18} className="text-silver/50 group-hover:text-gold transition-colors duration-400" strokeWidth={1.4} />
                </div>
                <div className="mt-6 h-px w-full bg-white/10" />
                <h3 className="mt-6 font-display font-light text-xl leading-snug text-white">{d.title}</h3>
                <p className="mt-4 text-silver/60 font-body font-light text-sm leading-relaxed">{d.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.16}>
          <div className="h-full rounded-xl border border-gold/25 bg-gold/[0.04] p-7 flex flex-col justify-center" data-testid="deliverable-note">
            <Sparkles className="text-gold" size={22} />
            <p className="mt-4 font-body font-light text-sm text-silver/70 leading-relaxed">
              Each deliverable is shaped around your environment, objectives and requirements — designed to be
              practical, actionable and professionally structured.
            </p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// 14 — Our Approach timeline
export function Approach() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 60%", "end 60%"] });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <Section id="approach" className="bg-ink-surface">
      <SectionHeading overline="Our Approach" title={<>A structured, <span className="silver-text">consultancy-led</span> method.</>} />
      <div ref={ref} className="mt-16 relative pl-10 md:pl-0">
        {/* central line */}
        <div className="absolute left-[15px] md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-white/10">
          {!reduced && <motion.div style={{ height }} className="w-full bg-gradient-to-b from-gold-light to-gold-dark" />}
        </div>
        <div className="space-y-10 md:space-y-16">
          {APPROACH.map((step, i) => (
            <Reveal key={step.n} delay={0.05}>
              <div className={`relative md:grid md:grid-cols-2 md:gap-12 items-center ${i % 2 ? "md:text-left" : ""}`}>
                <div className={`absolute -left-[38px] md:left-1/2 md:-translate-x-1/2 top-1 h-4 w-4 rounded-full bg-gold ring-4 ring-ink-surface z-10`} />
                <div className={`${i % 2 === 0 ? "md:col-start-1 md:text-right md:pr-12" : "md:col-start-2 md:pl-12"}`} data-testid={`approach-${i}`}>
                  <span className="font-display font-light text-5xl gold-text">{step.n}</span>
                  <h3 className="mt-2 font-display font-light text-2xl text-white">{step.title}</h3>
                  <p className="mt-3 text-silver/60 font-body font-light text-sm leading-relaxed max-w-sm md:inline-block">{step.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

// 15 — Why Arco Orbis2 (bento)
const WHY_ICONS = [Compass, ShieldCheck, Target, Layers, Cpu, Award, Globe2, Sparkles];

export function WhyArco() {
  return (
    <Section id="why" className="bg-ink">
      <SectionHeading overline="Why Arco Orbis2" title={<>Considered advantages, <span className="gold-text">by design.</span></>} align="center" />
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
        {WHY.map((w, i) => {
          const Icon = WHY_ICONS[i];
          const feature = i === 0 || i === 6;
          return (
            <Reveal key={w.title} delay={(i % 4) * 0.06} className={feature ? "col-span-2" : ""}>
              <div className={`group h-full hairline rounded-xl bg-ink-surface/60 p-6 hover:border-gold/40 transition-colors duration-500 flex flex-col ${feature ? "md:p-8" : ""}`} data-testid={`why-${i}`}>
                <Icon className="text-gold" size={feature ? 30 : 24} strokeWidth={1.3} />
                <h3 className={`mt-5 font-display font-light text-white ${feature ? "text-2xl" : "text-lg"}`}>{w.title}</h3>
                <p className="mt-3 text-silver/60 font-body font-light text-sm leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
}
